import type { Ga4CallbackFailure } from '@store/reducers/ga4'
import { Ga4Actions } from '@store/reducers/ga4'
import { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'

import { deleteSearchParam, getSearchParam } from '@/utils/searchParams'

// Всё, что не «грант припаркован», для юзера значит одно: не подключилось.
// Незнакомое значение (обрезанный адрес, новая причина на бэкенде) попадает
// сюда же — молчать нельзя, молчание и есть баг, который чинится.
const asFailure = (reason: string): Ga4CallbackFailure =>
  reason === 'denied' || reason === 'expired' ? reason : 'error'

// Бэкенд-коллбэк Google OAuth редиректит на /{projectUuid} с query-параметрами:
// ?ga4=pending&ga4_pending=<id> — грант припаркован, надо завершить подключение
// аутентифицированным запросом; ?ga4=denied|expired|error — юзер отказал,
// ссылка протухла или обмен упал. Хук только конвертирует параметры в экшен и
// чистит URL — вся логика в саге.
export const useGa4Callback = () => {
  const dispatch = useDispatch()
  const { uuid: projectId } = useParams<{ uuid: string }>()

  useEffect(() => {
    // Артборд смонтирован и на корне — там живёт анонимный «Untitled project»,
    // и туда же уводит редирект с неподтверждённым state. Стор общий с
    // дашбордом, так что включённый там SEO-режим переехал бы в следующий
    // открытый проект. Коллбэк касается только конкретного проекта в адресе.
    if (!projectId) {
      return
    }

    const reason = getSearchParam('ga4')

    if (reason === null) {
      return
    }

    const pendingId = getSearchParam('ga4_pending')

    deleteSearchParam('ga4_pending')
    deleteSearchParam('ga4')

    if (reason === 'pending' && pendingId !== null) {
      dispatch(Ga4Actions.complete(pendingId))

      return
    }

    dispatch(Ga4Actions.callbackFailed(asFailure(reason)))
  }, [dispatch, projectId])
}
